import React from "react";
import { classNames } from "primereact/utils";
import { MetricTrend } from "../../../common/enums";
import { AppTooltip } from "../../../common/components/tooltip/AppTooltip";
import { SkeletonItem } from "../../../common/components/skeletons/SkeletonItem";
import { IconType } from "../../../common/icons/Icon";
import { MetricConfig, MetricData } from "../../../types/dashboard";
import styles from "./MetricCard.module.scss";

export interface MetricCardProps {
  config: MetricConfig;
  data?: MetricData | null;
  isLoading?: boolean;
  onClick?: () => void;
}

export const MetricCard = React.memo<MetricCardProps>(
  ({ config, data, isLoading = false, onClick }) => {
    const { title, icon, iconClass, description, hasDetail, isTextValue } =
      config;
    const value = data?.value;
    const trend = data?.trend;
    const comparisonValue = data?.comparison;

    // Map iconClass to proper CSS class names
    const getIconClass = () => {
      if (iconClass?.includes("iconAlert")) return styles.iconAlert;
      if (iconClass?.includes("iconUsers")) return styles.iconUsers;
      if (iconClass?.includes("iconRevenue")) return styles.iconRevenue;
      if (iconClass?.includes("iconEvents")) return styles.iconEvents;
      if (iconClass?.includes("iconPerformance")) return styles.iconPerformance;
      return styles.iconDefault;
    };
    const getTrendArrow = () => {
      if (!trend) return null;

      switch (trend) {
        case MetricTrend.Up:
          return "↗";
        case MetricTrend.Down:
          return "↘";
        case MetricTrend.Stable:
          return "→";
        default:
          return "→";
      }
    };

    const getTrendClass = () => {
      if (!trend) return "";

      switch (trend) {
        case MetricTrend.Up:
          return styles.trendUp;
        case MetricTrend.Down:
          return styles.trendDown;
        case MetricTrend.Stable:
          return styles.trendStable;
        default:
          return styles.trendStable;
      }
    };

    const formatComparison = () => {
      if (comparisonValue === null || comparisonValue === undefined) {
        return "Без промяна";
      }

      const sign = comparisonValue > 0 ? "+" : "";
      return `${sign}${comparisonValue.toFixed(1)}%`;
    };

    if (isLoading) {
      return (
        <div className={styles.metricCard}>
          <div className={styles.cardHeader}>
            <div className={classNames(styles.iconContainer, iconClass)}>
              <SkeletonItem isLoading={true} value="" width="24px" />
            </div>
            <div className={styles.titleContainer}>
              <SkeletonItem
                isLoading={true}
                value=""
                width="60%"
                className={styles.title}
              />
            </div>
          </div>

          <div className={styles.cardContent}>
            <div className={styles.valueContainer}>
              <SkeletonItem
                isLoading={true}
                value=""
                width="40%"
                className={styles.value}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(styles.metricCard, {
          [styles.clickable]: hasDetail && onClick,
        })}
        onClick={hasDetail ? onClick : undefined}
      >
        <div className={styles.cardHeader}>
          <div className={classNames(styles.iconContainer, getIconClass())}>
            <span className={styles.icon}>{icon}</span>
          </div>
          <div className={styles.titleContainer}>
            <h3 className={styles.title}>{title}</h3>
          </div>
        </div>

        <div className={styles.cardContent}>
          <div className={styles.valueContainer}>
            <span className={isTextValue ? styles.valueText : styles.value}>
              {value != null ? value : "--"}
            </span>
          </div>

          {/* Text metrics (e.g. a top product name) have no period-over-period comparison */}
          {!isTextValue && (
            <div className={styles.comparison}>
              {trend && (
                <span className={classNames(styles.trendArrow, getTrendClass())}>
                  {getTrendArrow()}
                </span>
              )}
              <span>{formatComparison()}</span>
            </div>
          )}
        </div>

        <div className={styles.iconsContainer}>
          {hasDetail && (
            <div className={styles.detailIndicator}>
              <span>👁️</span>
            </div>
          )}
          {description && (
            <AppTooltip
              content={description}
              iconType={IconType.Information}
              className={styles.infoTooltip}
            />
          )}
        </div>
      </div>
    );
  }
);
